<template>
  <div class="ugcs-list">
    <div v-if="loading" class="ugcs-empty">Loading…</div>
    <div v-else-if="!items.length" class="ugcs-empty">No sales yet.</div>

    <div v-else class="ugcs-rows">
      <div v-for="(item, index) in items" :key="item.id || index" class="ugcs-row">
        <div class="ugcs-thumb" :style="!(item.ugcAsset && item.ugcAsset.thumbnail_url) ? thumbStyle(item.ugcAsset ? item.ugcAsset.id : index) : null">
          <img
            v-if="item.ugcAsset && item.ugcAsset.thumbnail_url"
            :src="item.ugcAsset.thumbnail_url"
            class="ugcs-thumb-img"
            alt=""
          />
          <i
            v-else
            class="fal"
            :class="item.ugcAsset && item.ugcAsset.mime_type && item.ugcAsset.mime_type.startsWith('video') ? 'fa-play-circle' : 'fa-image'"
          ></i>
        </div>

        <div class="ugcs-info">
          <div class="ugcs-filename" :title="item.ugcAsset ? item.ugcAsset.original_filename : ''">
            {{ item.ugcAsset ? item.ugcAsset.original_filename : 'Deleted asset' }}
          </div>
          <div class="ugcs-rights">
            <i class="fal fa-shield-check"></i>
            <span
              v-if="item.ugcAsset && item.ugcAsset.extra_terms"
              class="ugcs-rights-text"
              :title="'Extra terms: ' + item.ugcAsset.extra_terms"
            >
              {{ formatRights(item.ugcAsset) }}
            </span>
            <span v-else>{{ formatRights(item.ugcAsset) }}</span>
            <span v-if="item.ugcAsset && item.ugcAsset.rights_period" class="ugcs-rights-period">
              · {{ formatRightsPeriod(item.ugcAsset.rights_period) }}
            </span>
          </div>
        </div>

        <div class="ugcs-brand">
          <div class="ugcs-brand-name">
            {{ item.ugcAsset && item.ugcAsset.business ? (item.ugcAsset.business.display_name || item.ugcAsset.business.company) : '-' }}
          </div>
          <div class="ugcs-col-label">Brand</div>
        </div>

        <div class="ugcs-date">
          <div class="ugcs-date-val">{{ item.purchased_at | utc_to_local_datetime }}</div>
          <div class="ugcs-col-label">Sold date</div>
        </div>

        <div class="ugcs-price">DKK {{ (item.base_price / 100) | local_numbers }}</div>

        <span class="ugcs-pill" :class="isActive(item) ? 'ugcs-status--success' : 'ugcs-status--error'">
          {{ isActive(item) ? 'Active' : 'Expired' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import utc_to_local_datetime from '@/helpers/filters/utc_to_local_datetime';
import local_numbers from '@/helpers/filters/local_numbers';

const RIGHTS_LABELS = {
  social_media: 'Social media',
  paid_ads: 'Paid ads',
  website: 'Website/webshop',
  email_marketing: 'Email marketing',
  landing_pages: 'Landing pages',
  print_outdoor: 'Print/outdoor',
};

const THUMB_GRADIENTS = [
  'linear-gradient(135deg,#f5c6c0,#e8a89e)',
  'linear-gradient(135deg,#c0d8f5,#9ec0e8)',
  'linear-gradient(135deg,#c0f5d0,#9ee8b4)',
  'linear-gradient(135deg,#f5ecc0,#e8d49e)',
  'linear-gradient(135deg,#e0c0f5,#c89ee8)',
];

export default {
  filters: { utc_to_local_datetime, local_numbers },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  methods: {
    formatRights(asset) {
      if (!asset?.usage_rights?.length) return '-';
      return asset.usage_rights.map(r => RIGHTS_LABELS[r] || r).join(', ');
    },
    formatRightsPeriod(value) {
      const map = {
        "30":        "30 days",
        "90":        "90 days",
        "180":       "6 months",
        "365":       "12 months",
        "unlimited": "Unlimited",
      };
      return map[value] || value || "-";
    },
    isActive(purchase) {
      const asset = purchase.ugcAsset;
      if (!asset) return false;
      if (asset.rights_period === 'unlimited') return true;
      if (!asset.available_until) return false;
      return new Date(asset.available_until) > new Date();
    },
    thumbStyle(id) {
      return { background: THUMB_GRADIENTS[(id || 0) % THUMB_GRADIENTS.length] };
    },
  },
};
</script>

<style scoped>
.ugcs-empty {
  text-align: center;
  padding: 32px;
  color: var(--ink-3, #aaa);
  font-style: italic;
  font-size: 13px;
}

.ugcs-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ugcs-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 12px);
  background: var(--surface, #fff);
  transition: box-shadow 0.15s;
}
.ugcs-row:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.06); }

.ugcs-thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ugcs-thumb i { font-size: 18px; color: rgba(255,255,255,0.8); }
.ugcs-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ugcs-info { flex: 2; min-width: 0; }
.ugcs-filename {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-1, #111);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}
.ugcs-rights {
  font-size: 11.5px;
  color: var(--ink-3, #aaa);
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.ugcs-rights i { color: var(--accent, #e05540); font-size: 11px; }
.ugcs-rights-text { cursor: help; border-bottom: 1px dashed currentColor; }
.ugcs-rights-period { color: var(--ink-4, #ccc); }

.ugcs-brand, .ugcs-date { flex: 1; min-width: 0; }
.ugcs-brand-name, .ugcs-date-val {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-1, #111);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ugcs-col-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-4, #ccc);
  margin-top: 2px;
}

.ugcs-price {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-1, #111);
  white-space: nowrap;
  flex-shrink: 0;
}

.ugcs-pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.ugcs-status--success { background: #1db87a; }
.ugcs-status--error { background: #e03e38; }

@media (max-width: 720px) {
  .ugcs-row { flex-wrap: wrap; }
  .ugcs-brand, .ugcs-date { flex: 1 1 40%; }
  .ugcs-price, .ugcs-pill { margin-left: auto; }
}
</style>